import cloneDeep from 'lodash/cloneDeep'
import merge from 'lodash/merge'

import { getDefaultLocale, getLocaleCodeFromCountryLang } from '@src/i18n/locale.util'
import type { CountryCodeTBB, LangTBB, LocaleCode } from '@src/i18n/locales'

export type Dictionary = typeof EN_US

export const getDictionary = (_country: string, _lang: string) => {
  const defaultLocale = getDefaultLocale()
  const country = _country ?? defaultLocale.countryCodeLowercase
  const lang = _lang ?? defaultLocale.code
  const localeCode = getLocaleCodeFromCountryLang(country, lang)
  const dictionaries: Record<LocaleCode, Dictionary> = {
    'en-US': EN_US,
    'en-CA': EN_CA,
    'fr-CA': FR_CA,
    'en-GB': EN_GB,
  }
  const dictionary = dictionaries[localeCode as LocaleCode]
  return dictionary ?? dictionaries[defaultLocale.code]
}

export const EN_US = {
  'checkout-form-errors': {
    'firstName.required': 'Please enter a first name.',
    'firstName.invalid': 'Please enter a valid first name.',
    'lastName.required': 'Please enter a last name.',
    'lastName.invalid': 'Please enter a valid last name.',
    'address.required': 'Please enter an address.',
    'address.invalid': 'Please enter a valid address.',
    'addressNumber.invalid': 'Please enter a valid address.',
    'disclaimer.required': 'Please check the box below to proceed.',
    'zip.required': 'Please enter a zip code.',
    'zip.invalid': 'Please enter a valid zip code.',
    'city.required': 'Please enter a city.',
    'partner.invalid':
      'We do not recognize that BODi Partner information, please verify and try again.',
    'city.invalid': 'Please enter a valid city.',
    'state.required': 'Please select your state.',
    'cardFirstName.required': 'Please enter a first name.',
    'cardFirstName.invalid': 'Please enter a valid first name.',
    'cardLastName.required': 'Please enter a last name.',
    'cardLastName.invalid': 'Please enter a valid last name.',
    'cardNumber.required': 'Credit card number is required.',
    'cardNumber.invalid':
      'Please enter a valid credit card number for: American Express, VISA, Mastercard or Discover',
    'cardExpiration.required': 'Please enter an expiration date.',
    'cardExpiration.invalid': 'Please enter a valid expiration date.',
    'cvvCode.required': 'Please enter a valid CVV Code.',
    'cvvCode.forSavedPayment':
      'To use this card, enter the CVV number or security code in the field provided.',
    'cardToken.required': 'Please enter a valid card number and CVV code.',
    'discount.invalid': 'Code is either invalid, expired or you may not qualify.',
    'discount.duplicate':
      'The promo code you entered conflicts with a previously applied promo code and was not applied to the cart.',
    'discount.emailRequired':
      'Oops! Please enter your email address before applying this promo code.',
  },
  'checkout-form-success': {
    'partner.success': 'This confirms {{partner}} is my BODi Partner',
    'partner.disclaimer':
      'Only your contact and product information will be made available to your free BODi Partner who may reach out to help support you with your health and wellness goals. You can stop sharing your contact information at any time.',
  },
  'sign-up-form-errors': {
    'email.exists':
      '<span>You already own this digital product, <a href="/login-bodweb?locale=en_US">login</a> to view your account.<span>',
    'email.invalid': 'Please enter a valid email address.',
    'password.invalid': 'Invalid Password',
    'password.reused': 'The new password must be different than the last password.',
    'password.mustMatch': 'Your passwords must match!',
    'password.incorrect': 'Your old password was not entered correctly.',
    disclaimer:
      'By continuing, I agree to the <link1>Terms and Conditions<link1> and <link2>Privacy Policy<link2>.',
  },
  'sign-up-form': {
    email: 'Email',
    password: 'Password',
  },
  'set-password-form': {
    title: 'Access your BODi App Profile',
    cta: 'SET PASSWORD',
    ruleMinEightMaxTwentyChars: '8-20 characters',
    ruleOneUpper: 'One uppercase letter',
    ruleOneLower: 'One lowercase Letter',
    ruleOneNumber: 'One number',
    ruleNoBannedChars: 'No ’_\\/“<>, characters or spaces',
    checklist: JSON.stringify([
      'Access content in the BODi app',
      'Manage your account details',
      'View previous orders',
      'Manage memberships and subscriptions',
    ]),
    'confirmation-title': 'All set! Get the app.',
    'confirmation-title-ios': 'Launch your fitness journey',
    'confirmation-subtitle': 'Enjoy BODi on your phone, tablet, bike or computer!',
    'confirmation-subtitle-ios': 'Access BODi on your phone, tablet, TV, or computer',
    'confirmation-cta': 'GET STARTED',
  },
  'checkout-disclaimer': {
    'disclaimer-question':
      'By checking the box, you acknowledge you have read, understand, and agree to the terms below:',
    'disclaimer-heading': 'By continuing, I agree to the',
    'placing-order-disclosure':
      'By placing your order, you agree to the BODi <link1>Terms and Conditions<link1> and <link2>Privacy Policy<link2>.',
    'terms-and-conditions-link': 'Terms and Conditions',
    'privacy-policy-link': 'Privacy Policy',
    'auth-disclaimer':
      'For fraud prevention, you may see a $1.01 temporary authorization on your account, which will be removed once your payment is verified.',
  },
  'login-form': {
    title: 'Login',
    email: 'Email',
    password: 'Password',
    cta: 'Login',
    'forgot-password': 'Forgot Password',
    'email-required': 'Email address is required',
    'email-invalid': 'Please enter a valid email address',
    'password-required': 'Password is required',
    'invalid-credentials': 'The email or password you entered is incorrect. Please try again.',
    'unknown-error': `Oops… we've got an error.`,
  },
  'checkout-page': {
    'contact-information': 'Contact Information',
    'payment-method': 'Payment Method',
    'credit-card': 'Credit Card',
    payment: 'Payment',
    'first-name': 'First Name',
    'last-name': 'Last Name',
    'card-number': 'Card Number',
    cvv: 'CVV',
    'mm-yy': 'MM/YY',
    'billing-zip-code': 'Billing zip code',
    'order-summary': 'Order summary',
    'your-items': 'Your Items ({{count}})',
    promo: 'Promo',
    edit: 'Edit',
    subtotal: 'Subtotal',
    'estimated-tax': 'Estimated Tax',
    total: 'Your cost today',
    'total-savings': 'Total Savings',
    'bodi-got-you-covered': 'BODi’s got you covered',
    'your-orders-are-safe-and-secure-with-bodi': 'Your orders are safe and secure with BODi!',
    'secured-transactions': 'Secured transactions',
    '100-satisfaction-guarantee': '100% satisfaction guarantee',
    'no-hassel-refunds': 'No-hassle refunds',
    'order-details': 'Order details',
    shipping: 'Shipping and Handling',
    '30-Day-Money-Back Guarantee': '30-Day-Money-Back Guarantee', // From backend
    'place-order': 'Place Order',
    contact: 'Contact',
    email: 'Email',
    'shipping-address': 'Shipping address',
    address: 'Address',
    'apartment-suite-etc': 'Apartment, suite, etc (Optional)',
    city: 'City',
    region: 'State',
    'postal-code': 'Zip',
    'delivery-method': 'Shipping options',
    'standard-delivery': 'Standard Delivery',
    free: 'Free',
    'ships-in-estimated-days': 'Ships in {{estimatedDelivery}}',
    'ships-in-3-5-business-days': 'Ships in 3-5 business days',
    'express-delivery': 'Express delivery',
    'ships-in-1-2-business-days': 'Ships in 1-2 business days',
    'same-as-shipping-address': 'Billing address same as shipping',
    'billing-address': 'Billing Address',
    'add-promo-code': 'Add promo code',
    apply: 'Apply',
    applied: 'Applied',
    'total-discounts': 'Total Discounts',
    'estimated-gst-hst': 'Estimated GST/HST',
    'estimated-pst-qst-rst': 'Estimated PST/QST/RST',
    'placing-order-acknowledge':
      'By checking the box, you acknowledge you have read, understand, and agree to the terms below:',
    'were-you-referred-by-a-bodi-partner': 'Were you referred by a BODi Partner?',
    'partner-id-or-email': 'Partner ID or Email (Optional)',
    'not-calculated': 'Not calculated',
    quantity: 'Qty',
    'placing-order-message-1': 'Warming up...',
    'placing-order-message-2': 'This is the moment we train for',
    'placing-order-message-3': 'Relax your shoulders, we’re almost there',
    'placing-order-message-4': 'Getting one more rep in',
    'error-unrecoverable-title': 'Oops… we’ve got an error',
    'error-unrecoverable-message':
      'So sorry but we had to remove the items in your cart. Try starting your purchase process again.',
    'error-unrecoverable-cta': 'Return to homepage',
    'error-retry-title': 'Oops… try that again',
    'error-retry-message': 'Something went wrong behind the scenes. Try that move again.',
    'error-retry-cta': 'Try again',
    'error-eligibility-title': 'Looks like you already have that!',
    'error-eligibility-message': 'Check out the full BODi library of digital purchases.',
    'error-eligibility-cta': 'Browse Programs',
    'error-payment-message': 'Please update your payment and try again',
    'error-apple-pay-payment-message':
      'Apple Pay could not be initialized. Please verify Apple Pay is correctly setup on your device and try again later.',
    'error-invalid-items-title': 'We’ve removed one or more items from your cart.',
    'error-invalid-items-message': `You're not eligible for this product, or the availability has changed.`,
    'error-invalid-items-cta': 'Ok',
    'remove-line-item': 'Remove item',
    'cybersource-3ds-step-up-title': 'Verify Your Credit Card Information',
    login: 'Login',
    'sign-out': 'Sign Out',
    'add-new': 'Add New',
  },
  'order-confirmation': {
    yourOrderIsConfirmed: 'Your order is confirmed',
    orderNumber: 'Order number: {{orderNumber}}',
    youWillGetConfirmationAt: 'You’ll get an order confirmation soon at: {{customerEmail}}',
    printReceipt: 'Print receipt',
    yourOrder: 'Your Order',
    subtotal: 'Subtotal',
    estimatedTax: 'Estimated tax',
    total: 'Total',
    billingAndPayment: 'Billing and Payment',
    cardDetails: `{{cardBrand}} ending in {{cardNumber}}, expires {{cardExpiration}}`,
    'total-savings': 'Total Savings',
    shipping: 'Shipping and Handling',
    'error-unknown-title': 'Oops… try that again',
    'error-unknown-message': 'Something went wrong behind the scenes. Try that move again.',
    'error-unknown-cta': 'Try again',
  },
  test: {
    title: 'Home of the Commerce Web',
  },
  common: {
    currency: '${{amount}}',
    saving: 'Saving',
    suggestions: 'Suggestions',
    and: 'and',
    'bodi.com': 'bodi.com',
    'date-pattern': 'MM/DD/YYYY',
    'partner-placeholder': 'Partner ID or Email (optional)',
    quantity: 'Qty: {{quantity}}',
    'error-unknown-title': 'Oops… we’ve got an error',
    'error-unknown-message': 'We were unable to add this to your cart.',
    'error-unknown-cta': 'Okay',
    'error-retry-title': 'Oops… try that again',
    'error-retry-message': 'Something went wrong behind the scenes. Try that move again.',
    'error-retry-cta': 'Try again',
    'identity-outage-title': 'Unable to Create Account',
    'identity-outage-cta': 'Homepage',
    'identity-outage-message': 'We were unable to create your account. Please try again later. ',
    'page-title-suffix': '| BODi by Beachbody',
    'view-cart': 'View Cart',
    description: 'Description',
    'how-to-use': 'How to Use',
    'key-ingredients': 'Key Ingredients',
    'error-quantity': 'You cannot add more than {{quantity}} of this item to your cart.',
    'shopping-with': 'Shopping with',
    'storefront-title': 'My BODi Storefront',
    'storefront-description': 'Shop my latest favorites from BODi.',
    free: 'Free',
    select: 'Select',
    login: 'Login',
    'login-join': 'Login/Join',
    'my-account': 'My Account',
    'my-dashboard': 'My Dashboard',
    'digital-purchases': 'Digital Purchases',
    favorites: 'Favorites',
    badges: 'Badges',
    tracking: 'Tracking',
    'my-progress': 'My Progress',
    with: 'with',
  },
  carousel: {
    'next-slide': 'Next Slide',
    'previous-slide': 'Previous Slide',
    'slide-number': 'Slide {{number}}',
  },
  'product-page': {
    shakeology: 'Shakeology',
    'facet-title-flavor': 'Flavor',
    'facet-title-packaging': 'Packaging',
    'facet-title-subscriptionFrequency': 'Frequency',
    'facet-title-keyIngredient': 'Key Ingredient',
    'facet-title-size': 'Size',
    quantity: 'Quantity',
    'add-to-cart': 'Add to Cart',
    'subscription-discount-disclaimer':
      '^Sale price for initial purchase only, subsequent renewals are {{amount}}',
    'pinch-to-zoom': 'Pinch to zoom',
  },
  'print-order': {
    orderConfirmation: 'Order Confirmation',
    congratulations: 'Congratulations!',
    orderProcessed: "We processed your order. You'll receive an email confirmation shortly.",
    billTo: 'Bill to',
    shipTo: 'Ship to',
    orderNumber: 'Order number: {{orderNumber}}',
    orderDate: 'Order date: {{orderDate}}',
    items: 'Items',
    quantity: 'Qty.',
    amount: 'Amount',
    subtotal: 'Subtotal',
    salesTax: 'Estimated Tax',
    shipping: 'Shipping and Handling',
    total: 'Total',
    shippingMethod: 'Delivery method: {{shippingMethod}}',
    totalDiscounts: 'Total discounts',
    billingZipCode: 'Billing zip code',
  },
  'geo-redirection-modal': {
    title: 'Change shipping country?',
    subtitle:
      "It seems you're in a different region. Would you like to update your shipping country?",
    'select-your-country': 'Select Your Country',
    confirm: 'Confirm',
    cancel: 'No Thanks',
  },
  programsPage: {
    programs: 'Programs',
  },
  accountPage: {
    account: 'Account',
    accountSubheader: 'Manage and update your BODi account info',
    accountSettings: 'Account Settings',
    shippingAddresses: 'Shipping Addresses',
    paymentMethods: 'Payment Methods',
    orders: 'Orders',
    subscriptions: 'Subscriptions & Memberships',
    name: 'Name',
    email: 'Email',
    emailChangeHint: 'To change your email address, please contact us',
    emailChangeFaqUrl: 'https://faq.bodi.com/us/en/contactus-support?category_id=1757',
    password: 'Password',
    language: 'Language',
    pricacyAndPreferences: 'Privacy and Preferences',
    updateYourPrivacyPreferences: 'Update your privacy and email preferences',
    edit: 'Edit',
    editName: 'Edit Name',
    firstName: 'First Name',
    lastName: 'Last Name',
    editPassword: 'Edit Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm New Password',
    editLanguage: 'Edit Language',
    update: 'Update',
    cancel: 'Cancel',
    nameUpdated: 'Your name has been updated',
    passwordUpdated: 'Your password has been updated',
    languageUpdated: 'Your language has been updated',
    unsupported: '(unsupported)',
    error: 'Unable to save changes, please try again later.',
  },
  accountAddresses: {
    noAddresses:
      'Currently, you don’t have any saved addresses. Click the “Add” button to save an address.',
    primary: 'Primary',
    add: 'Add',
    edit: 'Edit',
    update: 'Update',
    delete: 'Delete Address',
    deleteModalTitle: 'Delete shipping address?',
    deleteModalDescription:
      'Are you sure you want to delete this address? This action cannot be undone.',
    deleteModalCancel: 'Cancel',
    deleteModalDelete: 'Delete',
    addAddress: 'Add Address',
    editAddress: 'Edit Shipping Address',
    firstName: 'First Name',
    lastName: 'Last Name',
    country: 'Country',
    address: 'Address',
    addressNumber: 'Apartment, suite, etc. (optional)',
    city: 'City',
    state: JSON.stringify({
      US: 'State',
      CA: 'Province',
    }),
    zip: JSON.stringify({
      US: 'Zip',
      CA: 'Postal Code',
    }),
    setAsPrimaryAddress: 'Set as primary shipping address',
    save: 'Save',
    cancel: 'Cancel',
    addressCreated: 'Address created',
    addressUpdated: 'Address updated',
    addressDeleted: 'Address deleted',
    error: 'Unable to save changes, please try again later.',
    updateWarning:
      '<b>Important:</b> Changes to the address will not update any existing orders which you have placed. To update subscriptions, please go to the <a class="underline" href="{{subscriptionsAndMembershipsUrl}}">Memberships & Subscriptions</a> tab.',
  },
  accountPayments: {
    paymentMethods: 'Payment Methods',
    noPaymentMethods:
      'Currently, you don’t have any saved payment information. Click the “Add” button to save a payment method.',
    primary: 'Primary',
    expired: 'Expired',
    add: 'Add',
    edit: 'Edit',
    update: 'Update',
    delete: 'Delete Card',
    deleteModalTitle: 'Delete payment method?',
    deleteModalDescription:
      'Are you sure you want to delete this payment method? This action cannot be undone.',
    deleteModalCancel: 'Cancel',
    deleteModalDelete: 'Delete',
    addPaymentMethod: 'Add Payment Method',
    editPaymentMethod: 'Edit Payment Method',
    billTo: 'Bill to',
    sameAsShippingAddress: 'Same as primary shipping address',
    expires: 'Expires',
    expiresShort: 'Exp.',
    firstName: 'First Name',
    lastName: 'Last Name',
    country: 'Country',
    address: 'Address',
    addressNumber: 'Apartment, suite, etc. (optional)',
    city: 'City',
    state: JSON.stringify({
      US: 'State',
      CA: 'Province',
    }),
    zip: JSON.stringify({
      US: 'Zip',
      CA: 'Postal Code',
    }),
    setAsPrimaryPayment: 'Set as primary payment method',
    save: 'Save',
    cancel: 'Cancel',
    paymentMethodCreated: 'Payment method created',
    paymentMethodUpdated: 'Payment method updated',
    paymentMethodDeleted: 'Payment method deleted',
    error: 'Unable to save changes, please try again later.',
    updateWarning:
      '<b>Important:</b> Changes to the Payment Methods will not update any existing orders which you have placed. To update subscriptions, please go to the <a class="underline" href="{{subscriptionsAndMembershipsUrl}}">Memberships & Subscriptions</a> tab.',
  },
  'cart-page': {
    'your-cart': 'Your cart',
    'cart-empty': 'Your cart is currently empty',
    'cart-empty-cta': 'Continue Shopping',
    'continue-to-checkout': 'Continue to Checkout',
    'order-summary': 'Order Summary',
    subtotal: 'Subtotal',
    'subtotal-disclaimer': '*Shipping & taxes calculated at checkout',
  },
  'forgot-password': {
    email: 'Email',
    'reset-password': 'Reset your password',
    'enter-email':
      'Enter the email address you used to sign up and we will send you an email with how to set or reset your password',
    'send-email': 'Send Email',
    'email-sent': 'Email Sent',
    'send-instructions':
      'If an account with {{email}} exists, we’ll send reset instructions to that address.',
    'help-center':
      'Didn’t get an email? Please check your <a href="https://faq.bodi.com/us/en/answer?Id=1273" target="_blank">help center</a>.',
    'enter-password': 'Enter your new password',
    submit: 'Submit',
    disclaimer:
      'By continuing, I agree to the <a href="{{termsUrl}}" target="_blank">Terms and Conditions</a> and <a href="{{privacyUrl}}" target="_blank">Privacy Policy</a>.',
    'link-expired': 'Link expired',
    'request-new-link': 'Please request a new password reset link.',
    'try-again': 'Try again',
    'password-updated': 'Your password has been updated',
    'login-now': 'We have updated your password. Time to get back in the game.',
    login: 'Log In',
  },
  'apple-pay': {
    'invalid-address': 'Shipping Address Needed',
  },
  'outage-splash': {
    heading: 'System under maintenance',
    description: 'Please excuse us, but this page is temporarily offline. Please try again later.',
  },
  countries: {
    US: 'United States',
    CA: 'Canada',
    GB: 'United Kingdom',
    FR: 'France',
  } satisfies Record<CountryCodeTBB, string>,
  languages: {
    en: 'English',
    fr: 'French',
    es: 'Spanish',
  } satisfies Record<LangTBB, string>,
}

export const EN_CA: Dictionary = {
  'checkout-form-errors': {
    'firstName.required': 'Please enter a first name.',
    'firstName.invalid': 'Please enter a valid first name.',
    'lastName.required': 'Please enter a last name.',
    'lastName.invalid': 'Please enter a valid last name.',
    'address.required': 'Please enter an address.',
    'address.invalid': 'Please enter a valid address.',
    'addressNumber.invalid': 'Please enter a valid address.',
    'disclaimer.required': 'Please check the box below to proceed.',
    'zip.required': 'Please enter a postal code.',
    'zip.invalid': 'Please enter a valid postal code.',
    'city.required': 'Please enter a city.',
    'partner.invalid':
      'We do not recognize that BODi Partner information, please verify and try again.',
    'city.invalid': 'Please enter a valid city.',
    'state.required': 'Please select your province.',
    'cardFirstName.required': 'Please enter a first name.',
    'cardFirstName.invalid': 'Please enter a valid first name.',
    'cardLastName.required': 'Please enter a last name.',
    'cardLastName.invalid': 'Please enter a valid last name.',
    'cardNumber.required': 'Credit card number is required.',
    'cardNumber.invalid':
      'Please enter a valid credit card number for: American Express, VISA, Mastercard or Discover',
    'cardExpiration.required': 'Please enter an expiration date.',
    'cardExpiration.invalid': 'Please enter a valid expiration date.',
    'cvvCode.required': 'Please enter a valid CVV Code.',
    'cvvCode.forSavedPayment':
      'To use this card, enter the CVV number or security code in the field provided.',
    'cardToken.required': 'Please enter a valid card number and CVV code.',
    'discount.invalid': 'Code is either invalid, expired or you may not qualify.',
    'discount.duplicate':
      'The promo code you entered conflicts with a previously applied promo code and was not applied to the cart.',
    'discount.emailRequired':
      'Oops! Please enter your email address before applying this promo code.',
  },
  'sign-up-form-errors': {
    'email.exists':
      '<span>You already own this digital product, <a href="/login-bodweb?locale=en_CA">login</a> to view your account.<span>',
    'email.invalid': 'Please enter a valid email address.',
    'password.invalid': 'Invalid Password',
    'password.reused': 'The new password must be different than the last password.',
    'password.mustMatch': 'Your passwords must match!',
    'password.incorrect': 'Your old password was not entered correctly.',
    disclaimer:
      'By continuing, I agree to the <link1>Terms and Conditions<link1> and <link2>Privacy Policy<link2>.',
  },
  'sign-up-form': {
    email: 'Email',
    password: 'Password',
  },
  'set-password-form': {
    title: 'Access your BODi App Profile',
    cta: 'SET PASSWORD',
    ruleMinEightMaxTwentyChars: '8-20 characters',
    ruleOneUpper: 'One uppercase letter',
    ruleOneLower: 'One lowercase Letter',
    ruleOneNumber: 'One number',
    ruleNoBannedChars: 'No ’_\\/“<>, characters or spaces',
    checklist: JSON.stringify([
      'Access content in the BODi app',
      'Manage your account details',
      'View previous orders',
      'Manage memberships and subscriptions',
    ]),
    'confirmation-title': 'All set! Get the app.',
    'confirmation-title-ios': 'Launch your fitness journey',
    'confirmation-subtitle': 'Enjoy BODi on your phone, tablet, bike or computer!',
    'confirmation-subtitle-ios': 'Access BODi on your phone, tablet, TV, or computer',
    'confirmation-cta': 'GET STARTED',
  },
  'checkout-form-success': {
    'partner.success': 'This confirms {{partner}} is my BODi partner',
    'partner.disclaimer':
      'Only your contact and product information will be made available to your free BODi Partner who may reach out to help support you with your health and wellness goals. You can stop sharing your contact information at any time.',
  },
  'login-form': EN_US['login-form'],
  'checkout-disclaimer': {
    ...EN_US['checkout-disclaimer'],
    'disclaimer-question':
      'By checking the box, you acknowledge you have read, understand, and agree to the terms below:',
    'disclaimer-heading': 'By continuing, I agree to the',
    'placing-order-disclosure':
      'By placing your order, you agree to the BODi <link1>Terms and Conditions<link1> and <link2>Privacy Policy<link2>.',
    'terms-and-conditions-link': 'Terms and Conditions',
    'privacy-policy-link': 'Privacy Policy',
  },
  'checkout-page': {
    'contact-information': 'Contact Information',
    'payment-method': 'Payment Method',
    'credit-card': 'Credit Card',
    payment: 'Payment',
    'first-name': 'First Name',
    'last-name': 'Last Name',
    'card-number': 'Card Number',
    cvv: 'CVV',
    'mm-yy': 'MM/YY',
    'billing-zip-code': 'Billing postal code',
    'order-summary': 'Order summary',
    'your-items': 'Your Items ({{count}})',
    promo: 'Promo',
    edit: 'Edit',
    subtotal: 'Subtotal',
    'estimated-tax': 'Estimated Tax',
    total: 'Your cost today',
    'total-savings': 'Total Savings',
    'bodi-got-you-covered': 'BODi’s got you covered',
    'your-orders-are-safe-and-secure-with-bodi': 'Your orders are safe and secure with BODi!',
    'secured-transactions': 'Secured transactions',
    '100-satisfaction-guarantee': '100% satisfaction guarantee',
    'no-hassel-refunds': 'No-hassle refunds',
    'order-details': 'Order details',
    shipping: 'Shipping and Handling',
    '30-Day-Money-Back Guarantee': '30-Day-Money-Back Guarantee', // From backend
    'place-order': 'Place Order',
    contact: 'Contact',
    email: 'Email',
    'shipping-address': 'Shipping address',
    address: 'Address',
    'apartment-suite-etc': 'Apartment, suite, etc (Optional)',
    city: 'City',
    region: 'Province',
    'postal-code': 'Postal Code',
    'delivery-method': 'Shipping options',
    'standard-delivery': 'Standard Delivery',
    free: 'Free',
    'ships-in-estimated-days': 'Ships in {{estimatedDelivery}}',
    'ships-in-3-5-business-days': 'Ships in 3-5 business days',
    'express-delivery': 'Express delivery',
    'ships-in-1-2-business-days': 'Ships in 1-2 business days',
    'same-as-shipping-address': 'Billing address same as shipping',
    'billing-address': 'Billing Address',
    'add-promo-code': 'Add promo code',
    apply: 'Apply',
    applied: 'Applied',
    'total-discounts': 'Total Discounts',
    'estimated-gst-hst': 'Estimated GST/HST',
    'estimated-pst-qst-rst': 'Estimated PST/QST/RST',
    'placing-order-acknowledge':
      'By checking the box, you acknowledge you have read, understand, and agree to the terms below:',
    'were-you-referred-by-a-bodi-partner': 'Were you referred by a BODi Partner?',
    'partner-id-or-email': 'Partner ID or Email (Optional)',
    'not-calculated': 'Not calculated',
    quantity: 'Qty',
    'placing-order-message-1': 'Warming up...',
    'placing-order-message-2': 'This is the moment we train for',
    'placing-order-message-3': 'Relax your shoulders, we’re almost there',
    'placing-order-message-4': 'Getting one more rep in',
    'error-unrecoverable-title': 'Oops… we’ve got an error',
    'error-unrecoverable-message':
      'So sorry but we had to remove the items in your cart. Try starting your purchase process again.',
    'error-unrecoverable-cta': 'Return to homepage',
    'error-retry-title': 'Oops… try that again',
    'error-retry-message': 'Something went wrong behind the scenes. Try that move again.',
    'error-retry-cta': 'Try again',
    'error-eligibility-title': 'Looks like you already have that!',
    'error-eligibility-message': 'Check out the full BODi library of digital purchases.',
    'error-eligibility-cta': 'Browse Programs',
    'error-payment-message': 'Please update your payment and try again',
    'error-apple-pay-payment-message':
      'Apple Pay could not be initialized. Please verify Apple Pay is correctly setup on your device and try again later.',
    'error-invalid-items-title': 'We’ve removed one or more items from your cart.',
    'error-invalid-items-message': `You're not eligible for this product, or the availability has changed.`,
    'error-invalid-items-cta': 'Ok',
    'remove-line-item': 'Remove item',
    'cybersource-3ds-step-up-title': 'Verify Your Credit Card Information',
    login: 'Login',
    'sign-out': 'Sign Out',
    'add-new': 'Add New',
  },
  'order-confirmation': {
    yourOrderIsConfirmed: 'Your order is confirmed',
    orderNumber: 'Order number: {{orderNumber}}',
    youWillGetConfirmationAt: 'You’ll get an order confirmation soon at: {{customerEmail}}',
    printReceipt: 'Print receipt',
    yourOrder: 'Your Order',
    subtotal: 'Subtotal',
    estimatedTax: 'Estimated tax',
    total: 'Total',
    billingAndPayment: 'Billing and Payment',
    cardDetails: `{{cardBrand}} ending in {{cardNumber}}, expires {{cardExpiration}}`,
    'total-savings': 'Total Savings',
    shipping: 'Shipping and Handling',
    'error-unknown-title': 'Oops… try that again',
    'error-unknown-message': 'Something went wrong behind the scenes. Try that move again.',
    'error-unknown-cta': 'Try again',
  },
  test: {
    title: 'Home of the Commerce Web',
  },
  common: {
    currency: '${{amount}}',
    saving: 'Saving',
    suggestions: 'Suggestions',
    and: 'and',
    'bodi.com': 'bodi.com',
    'date-pattern': 'MM/DD/YYYY',
    'partner-placeholder': 'Partner ID or Email (optional)',
    quantity: 'Qty: {{quantity}}',
    'error-unknown-title': 'Oops… we’ve got an error',
    'error-unknown-message': 'We were unable to add this to your cart.',
    'error-unknown-cta': 'Okay',
    'error-retry-title': 'Oops… try that again',
    'error-retry-message': 'Something went wrong behind the scenes. Try that move again.',
    'error-retry-cta': 'Try again',
    'identity-outage-title': 'Unable to Create Account',
    'identity-outage-message': 'We were unable to create your account. Please try again later. ',
    'identity-outage-cta': 'Homepage',
    'page-title-suffix': '| BODi by Beachbody',
    'view-cart': 'View Cart',
    description: 'Description',
    'how-to-use': 'How to Use',
    'key-ingredients': 'Key Ingredients',
    'error-quantity': 'You cannot add more than {{quantity}} of this item to your cart.',
    'shopping-with': 'Shopping with',
    'storefront-title': 'My BODi Storefront',
    'storefront-description': 'Shop my latest favorites from BODi.',
    free: 'Free',
    select: 'Select',
    login: 'Login',
    'login-join': 'Login/Join',
    'my-account': 'My Account',
    'my-dashboard': 'My Dashboard',
    'digital-purchases': 'Digital Purchases',
    favorites: 'Favorites',
    badges: 'Badges',
    tracking: 'Tracking',
    'my-progress': 'My Progress',
    with: 'with',
  },
  'product-page': {
    shakeology: 'Shakeology',
    'facet-title-flavor': 'Flavour',
    'facet-title-packaging': 'Packaging',
    'facet-title-subscriptionFrequency': 'Frequency',
    'facet-title-keyIngredient': 'Key Ingredient',
    'facet-title-size': 'Size',
    quantity: 'Quantity',
    'add-to-cart': 'Add to Cart',
    'subscription-discount-disclaimer':
      '^Sale price for initial purchase only, subsequent renewals are {{amount}}',
    'pinch-to-zoom': 'Pinch to zoom',
  },
  carousel: EN_US.carousel,
  'print-order': {
    ...EN_US['print-order'],
    billingZipCode: 'Billing postal code',
  },
  'geo-redirection-modal': {
    ...EN_US['geo-redirection-modal'],
  },
  programsPage: merge(cloneDeep(EN_US.programsPage)),
  accountPage: merge(cloneDeep(EN_US.accountPage), {
    emailChangeFaqUrl: 'https://faq.bodi.com/ca/en/contactus-support?category_id=1757',
  }),
  accountAddresses: merge(cloneDeep(EN_US.accountAddresses)),
  accountPayments: merge(cloneDeep(EN_US.accountPayments)),
  'cart-page': {
    'your-cart': 'Your cart',
    'cart-empty': 'Your cart is currently empty',
    'cart-empty-cta': 'Continue Shopping',
    'continue-to-checkout': 'Continue to Checkout',
    'order-summary': 'Order Summary',
    subtotal: 'Subtotal',
    'subtotal-disclaimer': '*Shipping & taxes calculated at checkout',
  },
  'forgot-password': merge(cloneDeep(EN_US['forgot-password']), {
    'help-center':
      'Didn’t get an email? Please check your <a href="https://faq.bodi.com/ca/en/answer?Id=1273" target="_blank">help center</a>.',
  }),
  'apple-pay': {
    'invalid-address': 'Shipping Address Needed',
  },
  'outage-splash': {
    ...EN_US['outage-splash'],
  },
  countries: EN_US.countries,
  languages: EN_US.languages,
}

export const FR_CA: Dictionary = {
  'checkout-form-errors': {
    'firstName.required': 'Veuillez saisir un prénom.',
    'firstName.invalid': 'Veuillez saisir un prénom valide.',
    'lastName.required': 'Veuillez saisir un nom de famille.',
    'lastName.invalid': 'Veuillez saisir un nom de famille valide.',
    'address.required': 'Veuillez saisir une adresse.',
    'address.invalid': 'Veuillez saisir une adresse valide.',
    'addressNumber.invalid': 'Veuillez saisir une adresse valide.',
    'disclaimer.required': 'Coche la case ci-dessous pour continuer.',
    'zip.required': 'Veuillez saisir un code postal.',
    'zip.invalid': 'Veuillez saisir un code postal valide.',
    'city.required': 'Veuillez saisir une ville.',
    'partner.invalid':
      'Nous ne reconnaissons pas ces renseignements sur ce partenaire BODi, vérifie et essaie à nouveau.',
    'city.invalid': 'Veuillez saisir une ville valide.',
    'state.required': 'Veuillez sélectionner votre province.',
    'cardFirstName.required': 'Veuillez saisir un prénom.',
    'cardFirstName.invalid': 'Veuillez saisir un prénom valide.',
    'cardLastName.required': 'Veuillez saisir un nom de famille.',
    'cardLastName.invalid': 'Veuillez saisir un nom de famille valide.',
    'cardNumber.required': 'Numéro de carte de crédit requis.',
    'cardNumber.invalid':
      'Veuillez saisir un numéro de carte de crédit valide pour : American Express, VISA, Mastercard ou Discover.',
    'cardExpiration.required': 'Veuillez saisir une date d’expiration.',
    'cardExpiration.invalid': 'Veuillez saisir une date d’expiration valide.',
    'cvvCode.required': 'Saisis un code CVV valide.',
    'cvvCode.forSavedPayment':
      'Pour utiliser cette carte, saisissez le numéro CVV ou le code de sécurité dans le champ fourni.',
    'cardToken.required': 'Saisis un numéro de carte et un code CVV valides.',
    'discount.invalid': 'Le code est invalide ou expiré, ou tu n’es peut-être pas admissible.',
    'discount.duplicate':
      'Le code promo que tu as saisi n’a pas été appliqué au panier, car il est incompatible avec un autre code promo déjà appliqué.',
    'discount.emailRequired':
      'Oups! Veuillez entrer votre adresse courriel avant d’appliquer ce code promo.',
  },
  'sign-up-form-errors': {
    'email.exists':
      '<span>Vous possédez déjà ce produit numérique, <a href="/login-bodweb?locale=fr-CA">connectez-vous</a> pour consulter votre compte.',
    'email.invalid': 'Veuillez saisir une adresse courriel valide.',
    'password.invalid': 'Mot de passe non valide',
    'password.reused': 'Le nouveau mot de passe doit être différent du dernier mot de passe.',
    'password.mustMatch': 'Veuillez saisir de nouveau la même valeur.',
    'password.incorrect': 'Votre ancien mot de passe na pas été saisi correctement.',
    disclaimer:
      'En continuant, j’accepte les <link1>Conditions générales<link1> et la <link2>Politique de confidentialité<link2>.',
  },
  'sign-up-form': {
    email: 'Courriel',
    password: 'Mot de passe',
  },
  'set-password-form': {
    title: 'Accéder à ton profil dans l’Application BODi',
    cta: 'DÉFINIR LE MOT DE PASSE',
    ruleMinEightMaxTwentyChars: 'De 8 à 20 caractères ',
    ruleOneUpper: 'Une lettre majuscule',
    ruleOneLower: 'Une lettre minuscule',
    ruleOneNumber: 'Un chiffre',
    ruleNoBannedChars: 'Pas de ’_\\/“<>, de caractères ni d’espaces',
    checklist: JSON.stringify([
      'Accéder au contenu de l’Application BODi',
      'Gérer les détails de ton compte',
      'Afficher les commandes antérieures',
      'Gérer les abonnements et les souscriptions',
    ]),
    'confirmation-title': 'Le tour est joué! Obtenir l’application.',
    'confirmation-title-ios': 'Commencez votre parcours de mise en forme',
    'confirmation-subtitle':
      'Profite de BODi sur ton téléphone, ta tablette, ton vélo ou ton ordinateur!',
    'confirmation-subtitle-ios':
      'Accédez à BODi sur votre téléphone, votre tablette ou votre ordinateur',
    'confirmation-cta': 'Démarrer',
  },
  'login-form': {
    title: 'Se connecter',
    email: 'Courriel',
    password: 'Mot de passe',
    cta: 'Se connecter',
    'email-required': 'L’adresse courriel est requise',
    'email-invalid': 'Veuillez saisir une adresse courriel valide',
    'password-required': 'Le mot de passe est requis',
    'forgot-password': 'Mot de passe oublié',
    'invalid-credentials': `L'adresse courriel ou le mot de passe que vous avez saisi est incorrect. Veuillez réessayer.`,
    'unknown-error': 'Oups… une erreur est survenue',
  },
  'checkout-form-success': {
    'partner.success': 'Cela confirme que {{partner}} est mon partenaire BODi.',
    'partner.disclaimer':
      'Only your contact and product information will be made available to your free BODi Partner who may reach out to help support you with your health and wellness goals. You can stop sharing your contact information at any time. FR',
  },
  'checkout-disclaimer': {
    'disclaimer-question':
      'En cochant la case, tu reconnais avoir lu, compris et accepté les conditions énoncées ci-dessous:',
    'disclaimer-heading': 'En passant ta commande, tu acceptes',
    'placing-order-disclosure':
      'En passant votre commande, vous acceptez <link1>les Conditions générales<link1> et <link2>la Politique de confidentialité<link2> de BODi.',
    'terms-and-conditions-link': 'les Conditions générales',
    'privacy-policy-link': 'la Politique de confidentialité',
    'auth-disclaimer':
      'Pour la prévention de la fraude, il se peut que vous voyiez une autorisation temporaire de 1,01 $ sur votre compte, qui sera supprimée dès que votre carte de paiement sera vérifiée',
  },
  'checkout-page': {
    'contact-information': 'Information de contact',
    'payment-method': 'Mode de paiement',
    'credit-card': 'Carte de Crédit',
    payment: 'Paiement',
    'first-name': 'Prénom',
    'last-name': 'Nom de famille',
    'card-number': 'Numéro de carte',
    cvv: 'CVV',
    'mm-yy': 'MM/AA',
    'billing-zip-code': 'Code postal de facturation',
    'order-summary': 'Sommaire de la commande',
    'your-items': 'Vos articles ({{count}})',
    promo: 'Code promo',
    edit: 'Modifier',
    subtotal: 'Sous-total',
    'estimated-tax': 'Estimation des taxes',
    total: 'Le montant à payer aujourd’hui',
    'total-savings': 'Total des économies',
    'bodi-got-you-covered': 'BODi s’occupe de tout',
    'your-orders-are-safe-and-secure-with-bodi': 'Vos commandes sont en sécurité avec BODi!',
    'secured-transactions': 'Transactions sécurisées',
    '100-satisfaction-guarantee': 'Satisfaction garantie à 100 %',
    'no-hassel-refunds': 'Remboursements sans conditions',
    'order-details': 'Détails de la commande',
    shipping: 'Frais de livraison et de manutention',
    '30-Day-Money-Back Guarantee': 'Garantie de remboursement de 30 jours', // From backend
    'place-order': 'Passer La Commande',
    contact: 'Coordonnées',
    email: 'Courriel',
    'shipping-address': 'Adresse de livraison',
    address: 'Adresse',
    'apartment-suite-etc': 'Appartement, bureau, etc. (facultatif)',
    city: 'Ville',
    region: 'Province',
    'postal-code': 'Code postal',
    'delivery-method': 'Options de livraison',
    'standard-delivery': 'Livraison standard',
    free: 'Gratuit',
    'ships-in-estimated-days': 'Livraison en {{estimatedDelivery}}',
    'ships-in-3-5-business-days': 'Expédition dans 3 à 5 jours ouvrables',
    'express-delivery': 'Livraison express',
    'ships-in-1-2-business-days': 'Expédition dans 1 à 2 jours ouvrables',
    'same-as-shipping-address': 'L’adresse de facturation est la même que l’expédition',
    'billing-address': 'Adresse de facturation',
    'add-promo-code': 'Ajouter un code promo',
    apply: 'Appliquer',
    applied: 'Appliqué',
    'total-discounts': 'Total des rabais',
    'estimated-gst-hst': 'Estimation de la TPS/TVH',
    'estimated-pst-qst-rst': 'Estimation de la TVP/TVQ/TVD',
    'placing-order-acknowledge':
      'En cochant la case, tu reconnais avoir lu, compris et accepté les conditions énoncées ci-dessous:',
    'were-you-referred-by-a-bodi-partner': 'As-tu été recommandé par un partenaire BODi?',
    'partner-id-or-email': 'Identifiant ou courriel du partenaire (facultatif)',
    'not-calculated': 'non calculé',
    quantity: 'Qté',
    'placing-order-message-1': 'Réchauffement...',
    'placing-order-message-2': 'C’est le moment pour lequel nous nous entraînons',
    'placing-order-message-3': 'Détends tes épaules, nous y sommes presque',
    'placing-order-message-4': 'Fais une répétition de plus',
    'error-unrecoverable-title': 'Oups… une erreur est survenue',
    'error-unrecoverable-message':
      'Vraiment désolés, mais nous avons dû supprimer les articles de ton panier. Essaie de recommencer ta procédure d’achat.',
    'error-unrecoverable-cta': 'Revenir à la page d’accueil',
    'error-retry-title': 'Oups… essaie encore une fois',
    'error-retry-message': 'Quelque chose a mal tourné dans les coulisses. Essaie encore une fois.',
    'error-retry-cta': 'Réessayer',
    'error-eligibility-title': 'On dirait que tu l’as déjà!',
    'error-eligibility-message': 'Consulte la bibliothèque complète des achats numériques de BODi.',
    'error-eligibility-cta': 'Parcourir les programmes',
    'error-payment-message': 'Mets à jour ton paiement et essaie à nouveau.',
    'error-apple-pay-payment-message':
      'Apple Pay n’a pas pu être initialisé. Veuillez vérifier que Apple Pay est correctement configuré sur votre appareil et réessayer plus tard.',
    'error-invalid-items-title': 'Nous avons enlevé un ou plusieurs articles de ton panier.',
    'error-invalid-items-message':
      'Tu n’es pas admissible à ce produit, ou il n’est plus disponible.',
    'error-invalid-items-cta': 'Ok',
    'remove-line-item': 'Supprimer l’élément',
    'cybersource-3ds-step-up-title': 'Vérifiez les informations de votre carte de crédit',
    login: 'Se connecter',
    'sign-out': 'Se déconnecter',
    'add-new': 'Ajouter un nouveau',
  },
  'order-confirmation': {
    yourOrderIsConfirmed: 'Ta commande est confirmée',
    orderNumber: 'N° de commande: {{orderNumber}}',
    youWillGetConfirmationAt:
      'Tu recevras bientôt une confirmation de commande à l’adresse suivante : {{customerEmail}}',
    printReceipt: 'Imprimer le reçu',
    yourOrder: 'Ta commande',
    subtotal: 'Sous-total',
    estimatedTax: 'Estimation des taxes',
    total: 'Total',
    billingAndPayment: 'Facturation et paiement',
    cardDetails: `{{cardBrand}} se termine par {{cardNumber}}, expires le {{cardExpiration}}`,
    'total-savings': 'Total des économies',
    shipping: 'Frais de livraison et de manutention',
    'error-unknown-title': 'Oups… essaie encore une fois',
    'error-unknown-message':
      'Quelque chose a mal tourné dans les coulisses. Essaie encore une fois.',
    'error-unknown-cta': 'Réessayer',
  },
  test: {
    title: 'Accueil du Commerce Web',
  },
  common: {
    currency: '${{amount}}',
    saving: 'Économisez',
    suggestions: 'Suggestions',
    and: 'et',
    'bodi.com': 'bodi.com',
    'date-pattern': 'MM/DD/YYYY',
    'partner-placeholder': 'Identifiant ou courriel du partenaire (facultatif)',
    quantity: 'Qty: {{quantity}}',
    'error-unknown-title': 'Oups… une erreur est survenu',
    'error-unknown-message': 'Nous n’avons pas pu ajouter ceci à ton panier.',
    'error-unknown-cta': "D'accord",
    'error-retry-title': 'Oups… essaie encore une fois',
    'error-retry-message': 'Quelque chose a mal tourné dans les coulisses. Essaie encore une fois.',
    'error-retry-cta': 'Réessayer',
    'identity-outage-title': 'Création d’un compte impossible',
    'identity-outage-message': 'Nous n’avons pas pu créer ton compte. Tu peux ressayer plus tard.',
    'identity-outage-cta': 'Page d’accueil',
    'page-title-suffix': '| BODi de Beachbody',
    'view-cart': 'Voir le panier',
    description: 'Description',
    'how-to-use': 'Directives d’utilisation',
    'key-ingredients': 'Ingrédients clés',
    'error-quantity':
      'Tu ne peux pas ajouter plus de {{quantity}} exemplaires de cet article à ton panier.',
    'shopping-with': 'Faire du shopping avec',
    'storefront-title': 'Ma vitrine BODi',
    'storefront-description': 'Achetez mes derniers favoris de BODi.',
    free: 'Gratuit',
    select: 'Sélectionner',
    login: 'Se connecter',
    'login-join': 'Se connecter',
    'my-account': 'Mon compte',
    'my-dashboard': 'Mon tableau de bord',
    'digital-purchases': 'Achats numériques',
    favorites: 'Favoris',
    badges: 'Badges',
    tracking: 'Suivi',
    'my-progress': 'Mes progrès',
    with: 'avec',
  },
  carousel: {
    'next-slide': 'Diapositive Suivante',
    'previous-slide': 'Diapositive Précédente',
    'slide-number': 'Diapositive {{number}}',
  },
  'product-page': {
    shakeology: 'Shakeology',
    'facet-title-flavor': 'Saveur',
    'facet-title-packaging': 'Emballage',
    'facet-title-subscriptionFrequency': 'Fréquence',
    'facet-title-keyIngredient': 'Ingrédient clé',
    'facet-title-size': 'Taille',
    quantity: 'Quantité',
    'add-to-cart': 'Ajouter au panier',
    'subscription-discount-disclaimer':
      '^Le prix réduit s’applique uniquement au premier achat, les renouvellements suivants sont de {{amount}}.',
    'pinch-to-zoom': 'Zoomer',
  },
  'print-order': {
    orderConfirmation: 'Confirmation de Commande',
    congratulations: 'Félicitations!',
    orderProcessed:
      'Nous avons traité ta commande. Tu recevras un courriel de confirmation sous peu.',
    billTo: 'Facturé à',
    shipTo: 'Expédier à',
    orderNumber: 'N° de commande: {{orderNumber}}',
    orderDate: 'Date de la commande: {{orderDate}}',
    items: 'Articles',
    quantity: 'Qté',
    amount: 'Montant',
    subtotal: 'Sous-total',
    salesTax: 'Estimation des taxes',
    shipping: 'Frais de livraison et de manutention',
    total: 'Total',
    shippingMethod: 'Méthode de livraison: {{shippingMethod}}',
    totalDiscounts: 'Total des rabais',
    billingZipCode: 'Code postal de facturation',
  },
  'geo-redirection-modal': {
    title: 'Changer le pays de livraison?',
    subtitle:
      'Il semblerait que tu sois dans une autre région. Veux-tu modifier ton pays de livraison?',
    'select-your-country': 'Sélectionne ton pays',
    confirm: 'Confirmer',
    cancel: 'Non merci',
  },
  programsPage: {
    programs: 'Programmes',
  },
  accountPage: {
    account: 'Compte',
    accountSubheader: 'Gérer et mettre à jour les renseignements de ton compte BODi',
    accountSettings: 'Paramètres du compte',
    shippingAddresses: 'Adresses de livraison',
    paymentMethods: 'Modes de paiement',
    orders: 'Mes commandes',
    subscriptions: 'Abonnements & Adhésions',
    name: 'Nom',
    email: 'Courriel',
    emailChangeHint: 'Pour modifier ton adresse courriel, contacte nous',
    emailChangeFaqUrl: 'https://faq.bodi.com/ca/fr/contactus-support?category_id=1757',
    password: 'Mot de passe',
    language: 'Langue',
    pricacyAndPreferences: 'Confidentialité et préférences',
    updateYourPrivacyPreferences:
      'Mettre à jour tes préférences en matière de confidentialité et de courrier électronique',
    edit: 'Modifier',
    editName: 'Modifier le nom',
    firstName: 'Prénom',
    lastName: 'Nom de famille',
    editPassword: 'Modifier le mot de passe',
    currentPassword: 'Mot de passe actuel',
    newPassword: 'Nouveau mot de passe',
    confirmPassword: 'Confirmer le nouveau mot de passe',
    editLanguage: 'Modifier la langue',
    update: 'Mettre à jour',
    cancel: 'Annuler',
    nameUpdated: 'Ton nom a été mis à jour',
    passwordUpdated: 'Ton mot de passe a été mis à jour',
    languageUpdated: 'Ta langue a été mise à jour',
    unsupported: '(incompatible)',
    error: 'Impossible d’enregistrer les modifications, tu peux réessayer plus tard.',
  },
  accountAddresses: {
    noAddresses:
      'Tu n’as actuellement aucune adresse enregistrée. Clique sur le bouton « Ajouter » pour en enregistrer une.',
    primary: 'Principal',
    add: 'Ajouter',
    edit: 'Modifier',
    update: 'Mettre à jour',
    delete: 'Supprimer l’adresse',
    deleteModalTitle: 'Supprimer l’adresse de livraison?',
    deleteModalDescription:
      'Souhaites-tu vraiment supprimer cette adresse? Cette action est irréversible.',
    deleteModalCancel: 'Annuler',
    deleteModalDelete: 'Supprimer',
    addAddress: 'Ajouter une adresse',
    editAddress: 'Modifier l’adresse de livraison',
    firstName: 'Prénom',
    lastName: 'Nom de famille',
    country: 'Pays',
    address: 'Adresse',
    addressNumber: 'Appartement, bureau, etc. (facultatif)',
    city: 'Ville',
    state: JSON.stringify({
      US: 'État',
      CA: 'Province',
    }),
    zip: JSON.stringify({
      US: 'Code postal',
      CA: 'Code postal',
    }),
    setAsPrimaryAddress: 'Définir comme adresse de livraison principale',
    save: 'Enregistrer',
    cancel: 'Annuler',
    addressCreated: 'Adresse créée',
    addressUpdated: 'Adresse mise à jour',
    addressDeleted: 'Adresse supprimée',
    error: 'Impossible d’enregistrer les modifications, tu peux réessayer plus tard.',
    updateWarning:
      '<b>Important:</b> Les modifications apportées à l’adresse ne mettront pas à jour les commandes existantes que vous avez passées. Pour mettre à jour les abonnements, veuillez accéder à l’onglet <a class="underline" href="{{subscriptionsAndMembershipsUrl}}">Abonnements & Adhésions</a>.',
  },
  accountPayments: {
    paymentMethods: 'Modes de paiement',
    noPaymentMethods:
      'Tu n’as actuellement aucun moyen de paiement enregistré. Clique sur le bouton « Ajouter » pour enregistrer un moyen de paiement.',
    primary: 'Principal',
    expired: 'Expiré',
    add: 'Ajouter',
    edit: 'Modifier',
    update: 'Mettre à jour',
    delete: 'Supprimer la carte',
    deleteModalTitle: 'Supprimer le moyen de paiement?',
    deleteModalDescription: 'Tu es sûr de vouloir supprimer ce moyen de paiement?',
    deleteModalCancel: 'Annuler',
    deleteModalDelete: 'Supprimer',
    addPaymentMethod: 'Ajouter un moyen de paiement',
    editPaymentMethod: 'Modifier le moyen de paiement',
    billTo: 'Facturer à ',
    sameAsShippingAddress: 'Même adresse que l’adresse de livraison principale',
    expires: 'Expire le',
    expiresShort: 'Exp.',
    firstName: 'Prénom',
    lastName: 'Nom de famille',
    country: 'Pays',
    address: 'Adresse',
    addressNumber: 'Appartement, bureau, etc. (facultatif)',
    city: 'Ville',
    state: JSON.stringify({
      US: 'État',
      CA: 'Province',
    }),
    zip: JSON.stringify({
      US: 'Code postal',
      CA: 'Code postal',
    }),
    setAsPrimaryPayment: 'Définir comme moyen de paiement principal',
    save: 'Enregistrer',
    cancel: 'Annuler',
    paymentMethodCreated: 'Moyen de paiement créé',
    paymentMethodUpdated: 'Moyen de paiement mis à jour',
    paymentMethodDeleted: 'Moyen de paiement supprimé',
    error: 'Impossible d’enregistrer les modifications, tu peux réessayer plus tard.',
    updateWarning:
      '<b>Important:</b> Les modifications apportées au moyen de paiement ne mettront pas à jour les commandes existantes que vous avez passées. Pour mettre à jour les abonnements, veuillez accéder à l’onglet <a class="underline" href="{{subscriptionsAndMembershipsUrl}}">Abonnements & Adhésions</a>.',
  },
  'cart-page': {
    'your-cart': 'Ton panier',
    'cart-empty': 'Ton panier est actuellement vide',
    'cart-empty-cta': 'Poursuivre le magasinage',
    'continue-to-checkout': 'Passer à la caisse',
    'order-summary': 'Sommaire de la commande',
    subtotal: 'Sous-total',
    'subtotal-disclaimer': '*Livraison et taxes calculés à la caisse',
  },
  'forgot-password': {
    email: 'Adresse courriel',
    'reset-password': 'Réinitialiser ton mot de passe',
    'enter-email':
      'Saisissez l’adresse électronique que vous avez utilisée pour vous inscrire et nous vous enverrons un courriel de récupération du mot de passe.',
    'send-email': 'Envoyer le courriel',
    'email-sent': 'E-mail envoyé',
    'send-instructions':
      'Si un compte avec {{email}} existe, nous enverrons les instructions de réinitialisation à cette adresse.',
    'help-center':
      'Tu n’as pas reçu d’e-mail? Consulte notre <a href="https://faq.bodi.com/ca/fr/answer?Id=11786" target="_blank">centre d’assistance</a>.',
    'enter-password': 'Saisissez votre nouveau mot de passe',
    submit: 'Envoyer',
    disclaimer:
      'En continuant, j’accepte les <a href="{{termsUrl}}" target="_blank">Conditions générales</a> et la <a href="{{privacyUrl}}" target="_blank">Politique de confidentialité</a>.',
    'link-expired': 'Lien expiré',
    'request-new-link': 'Veuillez demander un nouveau lien de réinitialisation du mot de passe.',
    'try-again': 'Réessayer',
    'password-updated': 'Votre mot de passe a été mis à jour',
    'login-now': 'Nous avons mis à jour votre mot de passe. Il est temps de reprendre le jeu.',
    login: `S'IDENTIFIER`,
  },
  'apple-pay': {
    'invalid-address': 'Adresse de livraison requise',
  },
  'outage-splash': {
    heading: 'Système en maintenance',
    description:
      'Veuillez nous excuser, mais cette page est temporairement hors ligne. Veuillez réessayer plus tard.',
  },
  countries: {
    US: 'États-Unis',
    CA: 'Canada',
    GB: 'Royaume-Uni',
    FR: 'France',
  } satisfies Record<CountryCodeTBB, string>,
  languages: {
    en: 'Anglais',
    es: 'Espagnol',
    fr: 'Français',
  } satisfies Record<LangTBB, string>,
}

export const EN_GB: Dictionary = merge(cloneDeep(EN_US), {
  common: {
    currency: '£{{amount}}',
    'date-pattern': 'DD/MM/YYYY',
  },
  'checkout-form-errors': {
    'cardNumber.invalid': 'Please enter a valid credit card number for: VISA or Mastercard',
  },
  'checkout-page': {
    'estimated-tax': 'Estimated VAT',
  },
  'checkout-disclaimer': {
    'auth-disclaimer':
      'For fraud prevention, you may see a £1.01 temporary authorization on your account, which will be removed once your payment is verified.',
  },
  'order-confirmation': {
    estimatedTax: 'Estimated VAT',
  },
  'print-order': {
    salesTax: 'Estimated VAT',
  },
  'forgot-password': {
    'help-center':
      'Didn’t get an email? Please check your <a href="https://faq.bodi.com/gb/en/answer?Id=1273" target="_blank">help center</a>.',
  },
} as Dictionary)
